import { FunctionComponent as F, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import MaUTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { IState } from "../../core/store/store";
import Link from '@mui/material/Link';
import { updateDosings } from "../../core/store/protocol/protocol.slice";
import { updateNumericValues } from "../../core/http/protocol";
import { sortByPriority } from "../../utils/dosings";
import ProtocolTableHeader from "./ProtocolTableHeader";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { PRODUCTS } from "../../core/router/paths";
import { Grid, Typography } from "@mui/material";
import DraggableProtocolTableRow from "./DraggableProtocolTableRow";
import { ProtocolDosingRow } from "@protoplan/types";



interface Props
{
  protocolId: number,
  updateValue: (dosingId: number, value: number, label: keyof ProtocolDosingRow) => Promise<void>;
}


const ProtocolTable: F<Props> = (props: Props) =>
{
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dosings = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.dosings);
  const checked = !useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.dosings?.every(d => !d.checked));

  const user = useSelector((state: IState) => state.user);
  const budget = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.budget);
  const currencySymbol = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.currencySymbol);
  const protocolEdit = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.edit);
  const fees = useSelector((state: IState) => state.protocol.protocols
    .find(p => p.protocolId === props.protocolId)?.fees);

  const edit = protocolEdit === undefined || protocolEdit;

  const [isDragging, setIsDragging] = useState(false);

  if(!dosings)
    return <></>;

  const discounts = Boolean(dosings.filter(r => r.discounts?.length).length);

  const headings = [
    { Header: 'Checked', accessor: 'checked' },
    { Header: 'Supplement', accessor: 'supplement' },
    { Header: 'Brand', accessor: 'brandName' },
    { Header: 'Vendor', accessor: 'vendorName' },
    { Header: 'Dose', accessor: 'dose' },
    { Header: 'Unit', accessor: 'doseUnit' },
    { Header: 'Doses per day', accessor: 'dosesPerDay' },
    { Header: 'Days per month', accessor: 'daysPerMonth' },
    { Header: 'Products per month', accessor: 'productsPerMonth' },
    { Header: 'Cost per month', accessor: 'costPerMonth' },
    { Header: '', accessor: 'dosingId' },
    { Header: '', accessor: 'scrapeTime' },
    { Header: '', accessor: 'exchangeRate' },
    { Header: '', accessor: 'listingCurrencyCode' },
    { Header: '', accessor: 'userCurrencyCode' },
    { Header: '', accessor: 'deliveryPerListing' },
    { Header: '', accessor: 'listingCurrencySymbol' },
    { Header: '', accessor: 'amount' },
    { Header: '', accessor: 'doseUnitId' },
    { Header: '', accessor: 'productId' },
    { Header: '', accessor: 'recDoseUnitId' },
    { Header: '', accessor: 'amountUnitId' },
    { Header: '', accessor: 'unitOptions' }]
    .concat(discounts ? { Header: ' ', accessor: 'discounts' } : [])
    .concat({ Header: 'Notes', accessor: 'note' });



  const updatePriorities = (srcIndex: number, destIndex: number) =>
  {
    const newDosings = dosings.map(r => ({ ...r }));

    // Update priority value of dragged item
    newDosings[srcIndex].priority = destIndex;

    // Increment/deincrement priorities of in-between rows
    newDosings.forEach((row, i) =>
    {
      if(i === srcIndex)
        return;

      let deltaIndex = 0;
      // Increasing priority => priorities of in-between rows reduce by 1
      if(srcIndex < destIndex && i > srcIndex && i <= destIndex)
      {
        deltaIndex = -1;
      }
      // Increasing priority => priorities of in-between rows increase by 1
      else if(srcIndex > destIndex && i < srcIndex && i >= destIndex)
      {
        deltaIndex = +1;
      }

      // Change priorities of in-between rows only
      if(deltaIndex)
      {
        row.priority = i + deltaIndex;
      }
    });

    dispatch(updateDosings({ protocolId: props.protocolId, dosings: sortByPriority(newDosings) }));
    updateNumericValues(newDosings.map(r => ({ dosingId: r.dosingId, value: r.priority })), "priority");
  };

  const onBeforeDragStart = () =>
  {
    setIsDragging(true);
  };

  const onDragEnd = (result: DropResult) =>
  {
    setIsDragging(false);

    const { destination, source } = result;
    if(!destination || destination.index === source.index)
      return;

    // Update rows' priorities in store & db
    updatePriorities(source.index, destination.index);
  };

  const totalCostsPerMonth = dosings.reduce((
    prev: { withFees: number, withoutFees: number },
    row: { costPerMonthWithFees?: number; costPerMonthWithoutFees?: number; checked?: boolean }) =>
  {
    if(!row.checked && !dosings.every(c => !c.checked))
      return prev;

    return {
      withFees: prev.withFees + (row.costPerMonthWithFees || 0),
      withoutFees: prev.withoutFees + (row.costPerMonthWithoutFees || 0) };
  }, { withFees: 0, withoutFees: 0 });

  let cumulativeCostPerMonth = 0;
  const protocolTableRows = dosings.map((row, rowIndex) =>
  {
    if(!dosings?.length)
      return;

    cumulativeCostPerMonth += (fees ? row.costPerMonthWithFees : row.costPerMonthWithoutFees) || 0;

    // Assign colour before render as otherwise prop expression evaulated at time of render when
    // cumulativeCostPerMonth = total
    const withinBudgetColour = (budget && cumulativeCostPerMonth > budget) ? 'red' : 'green';

    return <DraggableProtocolTableRow
      key={rowIndex}
      protocolId={props.protocolId}
      rowIndex={rowIndex}
      updateValue={props.updateValue}
      withinBudgetColour={withinBudgetColour}
      headings={headings}  />;
  });


  return (
    <MaUTable key='MaUTable' size='small'>

      <ProtocolTableHeader
        headings={headings}
        protocolId={props.protocolId}/>

        {edit ?
          <DragDropContext key='DragDropContext' onDragEnd={onDragEnd} onBeforeDragStart={onBeforeDragStart}>
            <Droppable key='Droppable' droppableId={'1'}>
              {provided =>
                <TableBody
                  key='provided'
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {protocolTableRows}
                  {provided.placeholder}
                </TableBody>}
            </Droppable>
          </DragDropContext> :
          <TableBody>
            {protocolTableRows}
          </TableBody>}

        <TableBody>
          <TableRow key='Add supplement'>
            <TableCell key='space1'/>
            <TableCell
              key='Add Supplement'
              style={{ height: '26px' }}
              colSpan={headings.filter(h => h.Header).length - 3 - Number(discounts)}>
              <Grid container direction='row' >
                <Grid item xs style={{ display: "flex", justifyContent: "flex-start" }}>
                  {(dosings.length < 127 && edit) ?
                    <Link
                      variant='body2'
                      underline='hover'
                      onClick={() => navigate(PRODUCTS)}
                      style={{ cursor: 'pointer'}}>
                      {"Add supplement"}
                    </Link> :
                    (((user.premium && dosings.length >= 127) || !edit) ?
                      <></> :
                      <Link
                        variant='body2'
                        target='_blank'
                        rel='noreferrer'
                        href={"https://www.patreon.com/Protoplan/"} >
                        {"Support Protoplan on Patreon to add more supplements"}
                      </Link>)}
                </Grid>
                <Grid item xs style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='body2' color='gray' justifyContent="flex-end" sx={{ mr: -2 }}>
                    {"Monthly cost " + (checked ? "of selected " : "") + ":"}
                  </Typography>
                </Grid>
              </Grid>
            </TableCell>
            <TableCell key={'Total cost per month' + (fees ? ' with fees' : '')}
              style={{ textAlign: 'right', minWidth: '54px', paddingLeft: "10px", paddingRight: "10px" }}>
              <TextTooltip
                lines={[
                  "Total cost " + (fees ? "and fees " : "") + "per month " +
                  (checked ? "of selected " : "") + "/" + currencySymbol]}>
                <b>
                  {(fees ? totalCostsPerMonth.withFees : totalCostsPerMonth.withoutFees).toFixed(2)}
                </b>
              </TextTooltip>
            </TableCell>
            {discounts ? <TableCell/> : <></>}
            <TableCell/>
          </TableRow>
        </TableBody>

    </MaUTable>);
};

export default ProtocolTable;
